import { maybe, rd, RemoteData } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { CircleDashed, EyeClosed, Lock, Undo2 } from 'lucide-react';
import { Button } from 'v5/design-system/Button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from 'v5/design-system/Tabs';
import {
  FeatureFlagConfig,
  FeatureFlagValueConfig
} from 'v5/services/front/FeatureManagementService/types';
import {
  DialogBody,
  DialogHeader,
  DialogTitle
} from '../../design-system/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from '../../design-system/Select';
import { Skeleton } from '../../design-system/Skeleton';
import { Switch } from '../../design-system/Switch';
import { SimpleTooltip } from '../../design-system/Tooltip';
import { Kbd } from '../../design-system/Kbd';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { FormDialogError } from 'v5/patterns/forms/dialogs/FormDialogError';
import { cn } from '../../platform/dom/cn';
import {
  FeatureFlagConfigView,
  FeatureManagementActionHandler
} from '../../services/front/FeatureManagementService/FeatureManagementService';

export interface FeatureFlagViewProps {
  view: RemoteData<FeatureFlagConfigView>;
  onAction: FeatureManagementActionHandler;
  onSave: () => Promise<void>;
  tab: string;
  onTabChange: (tab: string) => void;
}

function BooleanFlagValue({
  value,
  onChange
}: {
  value: Extract<FeatureFlagValueConfig<boolean>, { type: 'boolean' }>;
  onChange: (newValue: boolean) => void;
}) {
  return (
    <Switch
      checked={maybe.getOrElse(value.current, value.defaultValue)}
      onCheckedChange={onChange}
    />
  );
}

function EnumFlagValue({
  value,
  onChange
}: {
  value: Extract<FeatureFlagValueConfig<boolean>, { type: 'enum' }>;
  onChange: (
    newValue: Extract<
      FeatureFlagValueConfig<boolean>,
      { type: 'enum' }
    >['current']
  ) => void;
}) {
  return (
    <Select value={value.current ?? undefined} onValueChange={onChange}>
      <SelectTrigger size="sm" className="min-w-24">
        {value.current ?? value.defaultValue}
      </SelectTrigger>
      <SelectContent>
        {value.options.map(option => (
          <SelectItem size="lg" key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FlagValue({
  config,
  onChange
}: {
  config: FeatureFlagValueConfig<boolean>;
  onChange: (newValue: FeatureFlagValueConfig<boolean>['current']) => void;
}) {
  const value = config;
  switch (value.type) {
    case 'boolean':
      return <BooleanFlagValue value={value} onChange={onChange} />;
    case 'enum':
      return <EnumFlagValue value={value} onChange={onChange} />;
    default:
      throw new Error('Unsupported flag value type');
  }
}

interface FlagSwitchProps {
  config: FeatureFlagConfig;
  onChange: (newValue: FeatureFlagValueConfig<boolean>['current']) => void;
  onClear: () => void;
}

function FlagControl({ config, onClear, onChange }: FlagSwitchProps) {
  if (!config.supported) {
    return (
      <>
        <Lock className="text-fg-subtle size-4 justify-self-end" />
        <div />
      </>
    );
  }

  function getCanBeReset() {
    if (config.supported) {
      if (config.optional) {
        if (config.value.current !== null) {
          return true;
        }
      } else if (config.value.current !== config.value.defaultValue) {
        return true;
      }
    }
    return false;
  }
  const canBeReset = getCanBeReset();

  return (
    <>
      <div
        className={cn(
          'justify-self-end place-self-center flex flex-row items-center justify-center',
          config.value.current === null && 'opacity-50'
        )}
      >
        <FlagValue
          config={config.value as FeatureFlagValueConfig<boolean>}
          onChange={onChange}
        />
      </div>
      <SimpleTooltip
        title={
          canBeReset
            ? config.optional
              ? 'Unset the optional value'
              : 'Reset to default value'
            : config.optional
              ? 'This optional value is not set'
              : 'This is required and has a default value'
        }
      >
        <Button
          variant="ghost"
          className="-ml-4"
          layout="icon"
          size="sm"
          onClick={onClear}
          visuallyDisabled={!canBeReset}
        >
          {canBeReset ? (
            <Undo2 />
          ) : config.optional ? (
            <EyeClosed />
          ) : (
            <CircleDashed />
          )}
        </Button>
      </SimpleTooltip>
    </>
  );
}

export function FeatureFlagView({
  view,
  onAction,
  onSave,
  tab,
  onTabChange
}: FeatureFlagViewProps) {
  const saveMutation = promiseState.useMutation(onSave);

  return rd
    .journey(view)
    .wait(
      <div className="min-w-220 h-3/4">
        <div className="p-6">
          <Skeleton className="h-4 p-6 m-6" />
          <Skeleton className="h-4 p-6 m-6" />
          <Skeleton className="h-4 p-6 m-6" />
        </div>
      </div>
    )
    .catch(e => (
      <FormDialogError
        error={e}
        title="Feature flag error"
        description="Failed to load feature flag configuration"
      />
    ))
    .map(view => {
      const handleSave = () => {
        if (view.isDirty) {
          void saveMutation.track(void 0);
        }
      };

      return (
        <Tabs asChild value={tab} onValueChange={onTabChange}>
          <FormDialogContent
            disabled={!view.isDirty}
            onSubmit={handleSave}
            open={true}
            title={'Manage features'}
            renderHeader={header => (
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 py-2">
                  <div>{header}</div>
                  <TabsList className="self-start">
                    {view.featureFlagView.groups.map(group => (
                      <TabsTrigger
                        key={group.name}
                        value={group.name}
                        autoFocus={group.name === tab}
                      >
                        {group.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </DialogTitle>
              </DialogHeader>
            )}
            mutation={saveMutation.state}
            saveText="Save"
            className="min-w-220 h-3/4"
            renderDialogBody={body => (
              <DialogBody
                renderTopShadow={false}
                className="p-0 grid grid-cols-[1fr_repeat(4,auto)] gap-5"
              >
                {body}
              </DialogBody>
            )}
          >
            <div className="sticky top-0 grid grid-cols-subgrid rows-subgrid col-span-full px-6 py-3 bg-white z-1 [[data-has-top-shadow]_&]:shadow-sm shadow-none">
              <div className="font-semibold">Feature</div>
              <div className="text-center font-semibold justify-self-end">
                Backend
              </div>
              <div />
              <div className="text-center font-semibold justify-self-end">
                Frontend
              </div>
              <div />
            </div>
            {view.featureFlagView.groups.map(group => (
              <TabsContent
                key={group.name}
                value={group.name}
                asChild
                className="contents"
              >
                <div className="">
                  {group.entries.map(entry => (
                    <div
                      key={entry.featureId}
                      className="grid grid-cols-subgrid rows-subgrid col-span-full hocus:bg-bg-subtle px-6 py-3 items-center"
                    >
                      <div className={entry.isBypassed ? 'opacity-50' : ''}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold">{entry.featureName}</span>
                          {entry.shortcut && (
                            <SimpleTooltip title="Keyboard shortcut">
                              <Kbd className="text-xs p-1 bg-bg-subtle text-fg-default">
                                {entry.shortcut
                                  .split('+')
                                  .map(
                                    key =>
                                      key.charAt(0).toUpperCase() + key.slice(1)
                                  )
                                  .join(' ')}
                              </Kbd>
                            </SimpleTooltip>
                          )}
                          {entry.isBypassed && (
                            <SimpleTooltip title="This feature is turned off by code and will be removed soon.">
                              <div className="rounded-full inline-block text-sm bg-bg-subtle text-fg-default px-2 py-0.5 -my-0.5">
                                Bypassed
                              </div>
                            </SimpleTooltip>
                          )}
                        </div>
                        {entry.description && (
                          <p className="text-sm text-muted-foreground">
                            {entry.description}
                          </p>
                        )}
                      </div>

                      <FlagControl
                        config={entry.config.backend}
                        onChange={newValue => {
                          onAction({
                            type: 'setFeatureFlag',
                            source: 'backend',
                            featureId: entry.featureId,
                            value: newValue
                          });
                        }}
                        onClear={() =>
                          onAction({
                            type: 'clearFeatureFlag',
                            source: 'backend',
                            featureId: entry.featureId
                          })
                        }
                      />

                      <FlagControl
                        config={entry.config.frontend}
                        onChange={newValue =>
                          onAction({
                            type: 'setFeatureFlag',
                            source: 'frontend',
                            featureId: entry.featureId,
                            value: newValue
                          })
                        }
                        onClear={() =>
                          onAction({
                            type: 'clearFeatureFlag',
                            source: 'frontend',
                            featureId: entry.featureId
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </FormDialogContent>
        </Tabs>
      );
    });
}
